import { IssueTimelineDto } from '@issuemanager/api-client';
import { extractEmailFromUserString } from '@issuemanager/apps/client/utils';
import { useSeverityLocalizer } from '@issuemanager/client/features/issues';
import { EditableIssueFields } from '@issuemanager/constants';
import { Box, Stack, Typography } from '@mui/material';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { IssueTimelineAttachmentsEvents } from './issue-timeline-attachments-events';
import { TimelineEventTypography } from './timeline-event-typography';

interface IssueTimelineEditedEventsProps {
  events: IssueTimelineDto[];
}

const IMPACT_TRUNCATE_LENGTH = 30;

const truncateText = (text: string | undefined): string => {
  if (!text) return '';
  return text.length > IMPACT_TRUNCATE_LENGTH
    ? `${text.substring(0, IMPACT_TRUNCATE_LENGTH)}...`
    : text;
};

export function IssueTimelineEditedEvents({
  events,
}: IssueTimelineEditedEventsProps) {
  const { t } = useTranslation('common');
  const { getSeverityLabel } = useSeverityLocalizer();

  const renderRelatedOrgUnits = (
    from: string | undefined,
    to: string | undefined,
  ) => {
    const fromOrgUnits: string[] = from && JSON.parse(from);
    const toOrgUnits: string[] = to && JSON.parse(to);

    const addedOrgUnits = toOrgUnits?.filter(
      (orgUnit) => !fromOrgUnits?.includes(orgUnit),
    );

    const removedOrgUnits = fromOrgUnits?.filter(
      (orgUnit) => !toOrgUnits?.includes(orgUnit),
    );

    const elements: React.ReactNode[] = [];

    addedOrgUnits?.forEach((orgUnit) => {
      elements.push(
        <Stack direction='row' gap={1 / 2}>
          <Trans
            t={t}
            i18nKey='issueTimeline.relatedOrgUnitAdded'
            values={{ to: orgUnit }}
          />
        </Stack>,
      );
    });

    removedOrgUnits?.forEach((orgUnit) => {
      elements.push(
        <Stack direction='row' gap={1 / 2}>
          <Trans
            t={t}
            i18nKey='issueTimeline.relatedOrgUnitRemoved'
            values={{ from: orgUnit }}
          />
        </Stack>,
      );
    });

    return elements;
  };

  return (
    <Box>
      <Typography>{t('issueTimeline.issueEdited')}</Typography>

      <Box pl={5 / 2} pt={1} data-testid='issue-timeline-events'>
        {events.map((event) => {
          const { editedField, editedFrom, editedTo } =
            event.bodyData.parameters;

          const editedFromData = extractEmailFromUserString(editedFrom?.value);
          const editedToData = extractEmailFromUserString(editedTo?.value);

          let dueDateTimelineEventKey = 'issueTimeline.dueDateSet';
          if (editedFrom?.value?.length && editedTo?.value?.length) {
            dueDateTimelineEventKey = 'issueTimeline.dueDateChanged';
          } else if (editedFrom?.value?.length && !editedTo?.value?.length) {
            dueDateTimelineEventKey = 'issueTimeline.dueDateRemoved';
          }

          let impactTimelineEventKey: string | null = null;
          if (editedFrom?.value?.length && editedTo?.value?.length) {
            impactTimelineEventKey = 'issueTimeline.impactChanged';
          } else if (editedTo?.value?.length) {
            impactTimelineEventKey = 'issueTimeline.impactSet';
          } else if (editedFrom?.value?.length) {
            impactTimelineEventKey = 'issueTimeline.impactRemoved';
          }

          return (
            <>
              {editedField?.value === EditableIssueFields.SEVERITY && (
                <TimelineEventTypography data-testid='severity-timeline-event'>
                  <Trans
                    t={t}
                    i18nKey={
                      editedFrom?.value?.length
                        ? 'issueTimeline.severityChanged'
                        : 'issueTimeline.severitySet'
                    }
                    values={{
                      from: getSeverityLabel(editedFrom?.value),
                      to: getSeverityLabel(editedTo?.value),
                    }}
                  />
                </TimelineEventTypography>
              )}

              {editedField?.value === EditableIssueFields.DUE_DATE && (
                <TimelineEventTypography data-testid='due-date-timeline-event'>
                  <Trans
                    t={t}
                    i18nKey={dueDateTimelineEventKey}
                    values={{
                      from: editedFrom?.value,
                      to: editedTo?.value,
                    }}
                  />
                </TimelineEventTypography>
              )}

              {editedField?.value === EditableIssueFields.ISSUE_OWNER && (
                <Box data-testid='issue-owner-timeline-event'>
                  {editedFromData && editedToData ? (
                    <>
                      <TimelineEventTypography>
                        <Trans
                          t={t}
                          i18nKey='issueTimeline.issueOwnerChanged'
                          values={{
                            name: editedToData.name,
                            email: editedToData.email,
                          }}
                        />
                      </TimelineEventTypography>
                      <TimelineEventTypography>
                        <Trans
                          t={t}
                          i18nKey='issueTimeline.issueOwnerRemoved'
                          values={{
                            name: editedFromData.name,
                            email: editedFromData.email,
                          }}
                        />
                      </TimelineEventTypography>
                    </>
                  ) : (
                    <>
                      {editedFromData && (
                        <TimelineEventTypography>
                          <Trans
                            t={t}
                            i18nKey='issueTimeline.issueOwnerRemoved'
                            values={{
                              name: editedFromData.name,
                              email: editedFromData.email,
                            }}
                          />
                        </TimelineEventTypography>
                      )}
                      {editedToData && (
                        <TimelineEventTypography>
                          <Trans
                            t={t}
                            i18nKey='issueTimeline.issueOwnerAdded'
                            values={{
                              name: editedToData.name,
                              email: editedToData.email,
                            }}
                          />
                        </TimelineEventTypography>
                      )}
                    </>
                  )}
                </Box>
              )}

              {editedField?.value === EditableIssueFields.ISSUE_APPROVER && (
                <Box data-testid='issue-approver-timeline-event'>
                  {editedFromData && editedToData ? (
                    <>
                      <TimelineEventTypography>
                        <Trans
                          t={t}
                          i18nKey='issueTimeline.issueApproverChanged'
                          values={{
                            name: editedToData.name,
                            email: editedToData.email,
                          }}
                        />
                      </TimelineEventTypography>
                      <TimelineEventTypography>
                        <Trans
                          t={t}
                          i18nKey='issueTimeline.issueApproverRemoved'
                          values={{
                            name: editedFromData.name,
                            email: editedFromData.email,
                          }}
                        />
                      </TimelineEventTypography>
                    </>
                  ) : (
                    <>
                      {editedFromData && (
                        <TimelineEventTypography>
                          <Trans
                            t={t}
                            i18nKey='issueTimeline.issueApproverRemoved'
                            values={{
                              name: editedFromData.name,
                              email: editedFromData.email,
                            }}
                          />
                        </TimelineEventTypography>
                      )}
                      {editedToData && (
                        <TimelineEventTypography>
                          <Trans
                            t={t}
                            i18nKey='issueTimeline.issueApproverAdded'
                            values={{
                              name: editedToData.name,
                              email: editedToData.email,
                            }}
                          />
                        </TimelineEventTypography>
                      )}
                    </>
                  )}
                </Box>
              )}

              {editedField?.value === EditableIssueFields.ISSUE_MANAGER && (
                <Box data-testid='issue-manager-timeline-event'>
                  {editedFromData && editedToData ? (
                    <>
                      <TimelineEventTypography>
                        <Trans
                          t={t}
                          i18nKey='issueTimeline.issueManagerChanged'
                          values={{
                            name: editedToData.name,
                            email: editedToData.email,
                          }}
                        />
                      </TimelineEventTypography>
                      <TimelineEventTypography>
                        <Trans
                          t={t}
                          i18nKey='issueTimeline.issueManagerRemoved'
                          values={{
                            name: editedFromData.name,
                            email: editedFromData.email,
                          }}
                        />
                      </TimelineEventTypography>
                    </>
                  ) : (
                    <>
                      {editedFromData && (
                        <TimelineEventTypography>
                          <Trans
                            t={t}
                            i18nKey='issueTimeline.issueManagerRemoved'
                            values={{
                              name: editedFromData.name,
                              email: editedFromData.email,
                            }}
                          />
                        </TimelineEventTypography>
                      )}
                      {editedToData && (
                        <TimelineEventTypography>
                          <Trans
                            t={t}
                            i18nKey='issueTimeline.issueManagerAdded'
                            values={{
                              name: editedToData.name,
                              email: editedToData.email,
                            }}
                          />
                        </TimelineEventTypography>
                      )}
                    </>
                  )}
                </Box>
              )}

              {editedField?.value === EditableIssueFields.IMPACTED_ENTITY && (
                <TimelineEventTypography data-testid='related-org-unit-timeline-event'>
                  {renderRelatedOrgUnits(editedFrom?.value, editedTo?.value)}
                </TimelineEventTypography>
              )}

              {editedField?.value === EditableIssueFields.EXECUTION_TYPE && (
                <TimelineEventTypography data-testid='execution-type-timeline-event'>
                  <Trans
                    t={t}
                    i18nKey={
                      editedFrom?.value?.length
                        ? 'issueTimeline.executionTypeChanged'
                        : 'issueTimeline.executionTypeSet'
                    }
                    values={{
                      from: editedFrom?.value,
                      to: editedTo?.value,
                    }}
                  />
                </TimelineEventTypography>
              )}

              {editedField?.value === EditableIssueFields.ATTACHMENTS && (
                <IssueTimelineAttachmentsEvents
                  attachments={
                    event.bodyData.parameters.attachments?.value ?? []
                  }
                />
              )}

              {editedField?.value === EditableIssueFields.TITLE && (
                <TimelineEventTypography data-testid='title-timeline-event'>
                  <Trans
                    t={t}
                    i18nKey='issueTimeline.titleChanged'
                    values={{
                      from: editedFrom?.value,
                      to: editedTo?.value,
                    }}
                  />
                </TimelineEventTypography>
              )}

              {editedField?.value === EditableIssueFields.DESCRIPTION && (
                <TimelineEventTypography data-testid='description-timeline-event'>
                  <Trans
                    t={t}
                    i18nKey={
                      editedFrom?.value?.length
                        ? 'issueTimeline.descriptionChanged'
                        : 'issueTimeline.descriptionSet'
                    }
                    values={{
                      from: editedFrom?.value,
                      to: editedTo?.value,
                    }}
                  />
                </TimelineEventTypography>
              )}

              {editedField?.value === EditableIssueFields.IMPACT &&
                impactTimelineEventKey && (
                  <TimelineEventTypography data-testid='impact-timeline-event'>
                    <Trans
                      t={t}
                      i18nKey={impactTimelineEventKey}
                      values={{
                        from: truncateText(editedFrom?.value),
                        to: truncateText(editedTo?.value),
                      }}
                    />
                  </TimelineEventTypography>
                )}
            </>
          );
        })}
      </Box>
    </Box>
  );
}
